'use client'

import { useGuardedRouter } from '@/useGuardedRouter'
import { CategorySuggestion, SearchFacet } from '@/search/types'
import { QuickSearchResponseRecord } from '@/search/types'

interface CategorySuggestionsProps {
  facets?: SearchFacet[]
  results?: QuickSearchResponseRecord[]
}

export const CategorySuggestions = ({
  facets,
  results,
}: CategorySuggestionsProps) => {
  const router = useGuardedRouter()

  // Extract categories from facets
  const categoryFacet = facets?.find(f => f.field === 'categories')
  const categoryCounts = categoryFacet?.counts || []

  // Extract unique categories from results as fallback
  const resultCategories = new Map<string, number>()
  results?.forEach(result => {
    result.categories?.forEach(cat => {
      resultCategories.set(cat, (resultCategories.get(cat) || 0) + 1)
    })
  })

  // Combine facet categories with result categories
  const allCategories = new Map<string, number>()
  
  categoryCounts.forEach(count => {
    allCategories.set(count.value, count.count)
  })

  resultCategories.forEach((count, cat) => {
    if (!allCategories.has(cat)) {
      allCategories.set(cat, count)
    }
  })

  // Convert to array and sort by count
  const categorySuggestions: CategorySuggestion[] = Array.from(
    allCategories.entries()
  )
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => (b.count || 0) - (a.count || 0))
    .slice(0, 3) // Limit to top 3

  if (categorySuggestions.length === 0) {
    return null
  }

  const handleCategoryClick = (category: string) => {
    router.push(`/search?query=${encodeURIComponent(category)}`)
  }

  return (
    <div className="px-3 lg:px-2 py-2 border-b border-gray-200">
      <p className="text-xs font-semibold text-gray-500 uppercase mb-2">
        Categories
      </p>
      <div className="flex flex-wrap gap-2">
        {categorySuggestions.map(category => (
          <button
            key={category.name}
            onClick={() => handleCategoryClick(category.name)}
            className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-full transition-colors duration-150 border border-gray-200"
          >
            {category.name}
          </button>
        ))}
      </div>
    </div>
  )
}
